import { motion } from 'framer-motion';
import { Activity, X } from 'lucide-react';
import { type ReactElement, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';

import { NAVIGATION } from '@/app/router/navigation';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  mobileSidebarClosed,
  selectMobileSidebarOpen,
} from '@/features/ui';
import { cn } from '@/shared/lib/cn';

const DRAWER_WIDTH = 288;

export function MobileSidebar(): ReactElement | null {
  const open = useAppSelector(selectMobileSidebarOpen);
  const dispatch = useAppDispatch();
  const location = useLocation();

  useEffect(() => {
    if (!open) return;

    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dispatch(mobileSidebarClosed());
      }
    };

    document.addEventListener('keydown', handleKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [open, dispatch]);

  // Auto-close when the route changes (user tapped a link).
  useEffect(() => {
    if (open) {
      dispatch(mobileSidebarClosed());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only on pathname change
  }, [location.pathname]);

  if (!open) return null;

  const close = (): void => {
    dispatch(mobileSidebarClosed());
  };

  return createPortal(
    <div
      className="fixed inset-0 z-40 md:hidden"
      role="presentation"
      aria-label="Menú lateral móvil"
    >
      <motion.button
        type="button"
        aria-label="Cerrar menú"
        tabIndex={-1}
        onClick={close}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="absolute inset-0 cursor-default bg-background/70 backdrop-blur-sm"
      />

      <motion.aside
        initial={{ x: -DRAWER_WIDTH }}
        animate={{ x: 0 }}
        exit={{ x: -DRAWER_WIDTH }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        role="dialog"
        aria-modal="true"
        aria-label="Navegación principal"
        className="absolute left-0 top-0 flex h-full w-72 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-soft-3"
      >
        <div className="flex h-16 items-center justify-between gap-3 px-5">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-soft-2">
              <Activity className="h-4.5 w-4.5" />
            </span>
            <div>
              <div className="font-display text-base font-semibold leading-none tracking-tight">
                SISMA
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-sidebar-foreground/55">
                Salud Mental
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={close}
            aria-label="Cerrar menú"
            className="grid h-8 w-8 place-items-center rounded-md text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mx-3 h-px bg-sidebar-border" />

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-6">
            {NAVIGATION.map((section) => (
              <li key={section.label}>
                <div className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/45">
                  {section.label}
                </div>
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <MobileSidebarItem
                        to={item.to}
                        label={item.label}
                        icon={<item.icon className="h-4 w-4" />}
                        disabled={item.disabled === true}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </nav>
      </motion.aside>
    </div>,
    document.body,
  );
}

interface MobileSidebarItemProps {
  readonly to: string;
  readonly label: string;
  readonly icon: ReactElement;
  readonly disabled: boolean;
}

function MobileSidebarItem({
  to,
  label,
  icon,
  disabled,
}: MobileSidebarItemProps): ReactElement {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      aria-disabled={disabled}
      onClick={(e) => {
        if (disabled) e.preventDefault();
      }}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium',
          'transition-colors duration-150 ease-out-expo',
          disabled
            ? 'cursor-not-allowed text-sidebar-foreground/35'
            : isActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-soft-1'
              : 'text-sidebar-foreground/85 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !disabled ? (
            <span
              className="absolute inset-y-1 left-0 w-0.5 rounded-r-full bg-primary"
              aria-hidden
            />
          ) : null}
          <span className="grid place-items-center">{icon}</span>
          <span className="truncate">{label}</span>
          {disabled ? (
            <span className="ml-auto rounded-full bg-sidebar-border px-1.5 py-0.5 text-[9px] uppercase tracking-wider">
              próximo
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}
